import type { ReviewCommentsHandle } from "@/components/ai/review/use-review-comments";
import { CodeChangesPanel, type CodeChangesPanelRef } from "@/routes/environments/details/CodeChangesPanel";
import { GitStatusPanelCompact } from "@/routes/environments/details/GitStatusPanelCompact";
import { forwardRef, useImperativeHandle, useRef } from "react";

export type EnvironmentChangesViewRef = {
    scrollToFile: (filePath: string) => void;
    openFile: (filePath: string) => void;
    openFileWithPreview: (filePath: string) => void;
};

export type EnvironmentChangesViewProps = {
    environmentId: string;
    enableReviewComments?: boolean;
    /** File path queued for preview — consumed by CodeChangesPanel on mount */
    pendingSpecPreview?: string | null;
    onPendingSpecPreviewConsumed?: () => void;
    /** Shared review comments handle from the parent.
     *  When omitted, CodeChangesPanel creates its own via useReviewComments. */
    reviewComments?: ReviewCommentsHandle;
};

export const EnvironmentChangesView = forwardRef<EnvironmentChangesViewRef, EnvironmentChangesViewProps>(
    ({ environmentId, enableReviewComments = false, pendingSpecPreview, onPendingSpecPreviewConsumed, reviewComments }, ref) => {
        const codeChangesPanelRef = useRef<CodeChangesPanelRef>(null);

        useImperativeHandle(
            ref,
            () => ({
                scrollToFile: (filePath: string) => {
                    codeChangesPanelRef.current?.scrollToFile(filePath);
                },
                openFile: (filePath: string) => {
                    codeChangesPanelRef.current?.openFile(filePath);
                },
                openFileWithPreview: (filePath: string) => {
                    codeChangesPanelRef.current?.openFileWithPreview(filePath);
                },
            }),
            [],
        );

        return (
            <div className={"flex grow flex-col gap-4 @container"} data-testid="environment-changes-view">
                <GitStatusPanelCompact environmentId={environmentId} />

                <CodeChangesPanel
                    ref={codeChangesPanelRef}
                    environmentId={environmentId}
                    enableReviewComments={enableReviewComments}
                    pendingSpecPreview={pendingSpecPreview}
                    onPendingSpecPreviewConsumed={onPendingSpecPreviewConsumed}
                    reviewComments={reviewComments}
                />
            </div>
        );
    },
);
EnvironmentChangesView.displayName = "EnvironmentChangesView";
